import { useCallback } from 'react';
import { EventName, useEventsContext } from 'context/events/EventsContext';
import { toast, updateToast } from 'components/Toaster';
import getElapsedTimeMs from '../../util/getElapsedTimeMs';
import {
  AudioFileFormat,
  ScriptFormat,
} from '../../../server/graphql/__generated.types';
import { useDownloadProjectTakesMutation } from './operation.generated';
import performDownload from './performDownload';
import { usePromptsContext } from '../../context/prompts/PromptsContext';
import handleEntitlementDownloadError, {
  getGraphQLErrorCode,
} from './handleEntitlementDownloadError';

const useDownloadProjectTakes = () => {
  const [, dispatchPrompt] = usePromptsContext();
  const [download] = useDownloadProjectTakesMutation();
  const [eventTracking] = useEventsContext();

  const downloadProjectTakes = useCallback(
    async (inputs: {
      projectId: string;
      audioFileFormat: AudioFileFormat;
      includeScriptFormats?: Array<ScriptFormat>;
      setDownloadError?: (downloadError: string) => void;
      filename?: string | null;
      includeProjectName?: boolean;
      numberFilesAutomatically?: boolean;
      msGapDuration?: number;
      takeCount: number;
      onCombineDownloadError?: () => void;
      showTimeoutPrompt?: boolean;
    }) => {
      const {
        projectId,
        audioFileFormat,
        includeScriptFormats,
        setDownloadError,
        filename,
        includeProjectName,
        numberFilesAutomatically,
        msGapDuration,
        takeCount,
        onCombineDownloadError,
        showTimeoutPrompt,
      } = inputs;
      const startTime = performance.now();
      const errorMessage = `Failed to ${
        msGapDuration ? 'combine' : 'download'
      } project section`;

      const action = msGapDuration ? 'Combining' : 'Downloading';
      const toastId = toast({
        message: `${action} project sections`,
        type: 'loading',
      });

      try {
        await download({
          variables: {
            inputs: {
              projectId,
              audioFileFormat,
              includeScriptFormats,
              filename,
              includeProjectName,
              numberFilesAutomatically,
              msGapDuration,
            },
          },
          update: (proxy, { data }) => {
            if (!data?.downloadProjectTakes.success) {
              const errorMsg =
                data?.downloadProjectTakes.message ?? errorMessage;
              const combineErrorCode = data?.downloadProjectTakes.code;
              const shouldShowTimeoutPrompt =
                showTimeoutPrompt &&
                combineErrorCode === 'network_timeout_error' &&
                typeof onCombineDownloadError === 'function';

              handleEntitlementDownloadError({
                errorCode: combineErrorCode,
                toastId,
                errorMessage: errorMsg,
                dispatchPrompt,
                onTimeout: shouldShowTimeoutPrompt
                  ? onCombineDownloadError
                  : undefined,
              });

              if (
                !shouldShowTimeoutPrompt &&
                data?.downloadProjectTakes.code ===
                  'take_download_limit_exceeded' &&
                setDownloadError
              ) {
                setDownloadError(errorMsg);
              }

              // Track download
              eventTracking.track({
                type: EventName.ScriptStudioProjectDownload,
                data: {
                  projectId,
                  audioFormat: audioFileFormat,
                  success: false,
                  elapsedTimeMs: getElapsedTimeMs(startTime),
                  error: errorMsg,
                  includeScriptFormats,
                },
              });
            } else {
              const { data: downloadData } = data.downloadProjectTakes;

              // Combining takes, track event
              if (msGapDuration) {
                eventTracking.track({
                  type: EventName.TakeCombine,
                  data: {
                    count: takeCount,
                    msGapDuration,
                    filename: filename || '',
                  },
                });
              }

              eventTracking.track({
                type: EventName.ScriptStudioProjectDownload,
                data: {
                  projectId,
                  audioFormat: audioFileFormat,
                  includeScriptFormats,
                  success: true,
                  elapsedTimeMs: getElapsedTimeMs(startTime),
                  combinedTextLength:
                    downloadData?.downloadMetricsMetadata.combinedTextLength,
                  numberOfTakesJustConverted:
                    downloadData?.downloadMetricsMetadata
                      .numberOfClipsJustConverted,
                  zipFileAlreadyExisted:
                    downloadData?.downloadMetricsMetadata
                      .zipFileAlreadyExisted ?? false,
                },
              });

              performDownload({
                fileFormat: downloadData!.fileFormat,
                friendlyFileName: downloadData!.friendlyFileName,
                signedURL: downloadData!.signedURL,
              });

              updateToast(toastId ?? '', {
                message: `Successfully ${
                  msGapDuration ? 'combined' : 'downloaded'
                } project sections`,
                type: 'success',
              });
            }
          },
        });
      } catch (error) {
        const combineErrorCode = getGraphQLErrorCode(error);
        const shouldShowTimeoutPrompt =
          showTimeoutPrompt &&
          typeof onCombineDownloadError === 'function' &&
          combineErrorCode === 'network_timeout_error';

        handleEntitlementDownloadError({
          errorCode: combineErrorCode,
          toastId,
          errorMessage,
          dispatchPrompt,
          onTimeout: shouldShowTimeoutPrompt
            ? onCombineDownloadError
            : undefined,
        });
      }
    },
    [dispatchPrompt, download, eventTracking]
  );

  return downloadProjectTakes;
};

export default useDownloadProjectTakes;
